import { useEffect, useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Badge,
  Card,
  CardHeader,
} from '@fluentui/react-components';
import { EmployeeUpdateFormData, DetectedChanges } from '../../../hooks/useEmployeeUpdateForm';
import { apiClient, ListResponse } from '../../../api/client';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  section: {
    marginBottom: tokens.spacingVerticalL,
  },
  sectionTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground1,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  tableHeader: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: tokens.spacingVerticalS,
    textAlign: 'left',
    fontWeight: tokens.fontWeightSemibold,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  tableCell: {
    padding: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  before: {
    color: tokens.colorPaletteRedForeground1,
  },
  after: {
    color: tokens.colorPaletteGreenForeground1,
  },
  badgeList: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  noChanges: {
    textAlign: 'center',
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
});

interface ReviewStepProps {
  formData: EmployeeUpdateFormData;
  detectedChanges: DetectedChanges;
}

interface Software {
  _id: string;
  name: string;
}

const FIELD_LABELS: Record<string, string> = {
  jobTitle: 'Job Title',
  department: 'Department',
  mobilePhone: 'Mobile Phone',
  businessPhone: 'Business Phone',
  employeeId: 'Employee ID',
  employmentType: 'Employee Type',
  managerName: 'Manager',
  siteCode: 'Site',
};

const GROUP_NAMES: Record<string, string> = {
  [import.meta.env.VITE_GROUP_VPN || '']: 'VPN Access',
  [import.meta.env.VITE_GROUP_SAGE || '']: 'SAGE ERP',
  [import.meta.env.VITE_GROUP_ONBOARDING_USERS || '']: 'HR Onboarding System',
};

export function ReviewStep({ formData, detectedChanges }: ReviewStepProps) {
  const styles = useStyles();
  const [software, setSoftware] = useState<Software[]>([]);

  useEffect(() => {
    if (formData.requestedSoftware.length > 0) {
      apiClient
        .get<ListResponse<Software>>('/software')
        .then((response) => {
          const requestedSw = response.data.filter((sw) =>
            formData.requestedSoftware.includes(sw._id)
          );
          setSoftware(requestedSw);
        })
        .catch(console.error);
    }
  }, [formData.requestedSoftware]);

  if (!detectedChanges.hasChanges) {
    return (
      <div className={styles.noChanges}>
        <Text size={500}>No changes detected</Text>
        <Text>Please make at least one change before submitting.</Text>
      </div>
    );
  }

  const getOriginalValue = (field: keyof typeof FIELD_LABELS): string => {
    const original = formData.originalUserData;
    if (!original) return '-';

    switch (field) {
      case 'jobTitle':
        return original.jobTitle || '-';
      case 'department':
        return original.department || '-';
      case 'mobilePhone':
        return original.mobilePhone || '-';
      case 'businessPhone':
        return original.businessPhones?.[0] || '-';
      case 'employeeId':
        return original.employeeId || '-';
      case 'employmentType':
        return original.employeeType || '-';
      case 'managerName':
        return original.manager?.displayName || '-';
      case 'siteCode':
        return original.officeLocation || '-';
      default:
        return '-';
    }
  };

  const getCurrentValue = (field: keyof typeof FIELD_LABELS): string => {
    const value = formData[field as keyof EmployeeUpdateFormData];
    return value?.toString() || '-';
  };

  return (
    <div className={styles.container}>
      <Card>
        <CardHeader
          header={<Text size={500} weight="semibold">Review Changes</Text>}
          description={<Text>Please review all changes before submitting.</Text>}
        />
      </Card>

      {/* Employee Details Changes */}
      {Object.keys(detectedChanges.changes).length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Employee Details</div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.tableHeader}>Field</th>
                <th className={styles.tableHeader}>Before</th>
                <th className={styles.tableHeader}>After</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(detectedChanges.changes).map((field) => {
                const fieldKey = field as keyof typeof FIELD_LABELS;
                if (!FIELD_LABELS[fieldKey]) return null;

                return (
                  <tr key={field}>
                    <td className={styles.tableCell}>
                      <Text weight="semibold">{FIELD_LABELS[fieldKey]}</Text>
                    </td>
                    <td className={`${styles.tableCell} ${styles.before}`}>
                      {getOriginalValue(fieldKey)}
                    </td>
                    <td className={`${styles.tableCell} ${styles.after}`}>
                      {getCurrentValue(fieldKey)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Access Changes */}
      {(detectedChanges.groupsToAdd.length > 0 || detectedChanges.groupsToRemove.length > 0) && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Access Changes</div>
          {detectedChanges.groupsToAdd.length > 0 && (
            <div style={{ marginBottom: tokens.spacingVerticalM }}>
              <Text weight="semibold" style={{ display: 'block', marginBottom: tokens.spacingVerticalS }}>
                Groups to Add:
              </Text>
              <div className={styles.badgeList}>
                {detectedChanges.groupsToAdd.map((groupId) => (
                  <Badge key={groupId} appearance="filled" color="success">
                    {GROUP_NAMES[groupId] || groupId}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {detectedChanges.groupsToRemove.length > 0 && (
            <div>
              <Text weight="semibold" style={{ display: 'block', marginBottom: tokens.spacingVerticalS }}>
                Groups to Remove:
              </Text>
              <div className={styles.badgeList}>
                {detectedChanges.groupsToRemove.map((groupId) => (
                  <Badge key={groupId} appearance="filled" color="danger">
                    {GROUP_NAMES[groupId] || groupId}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Software Requests */}
      {formData.requestedSoftware.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>Software Requested</div>
          <div className={styles.badgeList}>
            {software.map((sw) => (
              <Badge key={sw._id} appearance="tint" color="informative">
                {sw.name}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
